import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImageViewer.module.scss";
import ImageViewerProps from "./ImageViewer.interface";

import { ImageLoadingState } from "./image-loading.enum";
import { useInView } from "react-intersection-observer";
import combineClasses from "helpers/combine-classes";
import preloadImage from "./helpers/preload-image";
import setImageOptions from "app/media/helpers/setImageOptions";
import Spinner from "components/Spinner/Spinner";
import { useWebPSupportCheck } from "react-use-webp-support-check";
import isBrowserContext from "helpers/isBrowserContext";

const ImageViewer: FunctionComponent<ImageViewerProps & {overwriteImageStyles?:string}> = (props) => {
    const { ref, inView, entry } = useInView({ triggerOnce: true, threshold: 0.2 });
    const supportsWebP = useWebPSupportCheck();

    const imgStyles = props?.overwriteImageStyles ? props.overwriteImageStyles : styles.image;

    const [imageUrl, setImageUrl] = useState<string>();
    const [imageLoadingState, setImageLoadingState] = useState<ImageLoadingState>(ImageLoadingState.NONE);

    const boundsClass =
        props.type === "containX"
            ? styles.contain_wide
            : props.type === "containY"
            ? styles.contain_long
            : props.type === "contain"
            ? props.aspectRatioX && props.aspectRatioY && props.aspectRatioX > props.aspectRatioY
                ? styles.contain_wide
                : styles.contain_long
            : undefined;
    const fallbackUrl = setImageOptions(
        props.src,
        400,
        supportsWebP ? "webp" : props.extensionType,
        props.crop,
        props.focalPoint,
        props.aspectRatioX && props.aspectRatioY ? props.aspectRatioY / props.aspectRatioX : undefined
    );

    {
        /* TODO: Update on resize */
    }

    useEffect(() => {
        if (entry && inView === true) {
            const boundingClientRect = entry.target.getBoundingClientRect();
            const boundingClientWidth = boundingClientRect?.width;
            const boundingClientHeight = boundingClientRect?.height;

            if (boundingClientWidth && boundingClientWidth > 0) {
                const clientWidth = boundingClientWidth * (window?.devicePixelRatio || 1);
                const clientHeight = boundingClientHeight * (window?.devicePixelRatio || 1);

                const heightRatio = props.aspectRatioX && props.aspectRatioY ? props.aspectRatioY / props.aspectRatioX : clientHeight / clientWidth;
                const imageUrlWithResizeOptions = setImageOptions(
                    props.src,
                    clientWidth,
                    supportsWebP ? "webp" : props.extensionType,
                    props.crop,
                    props.focalPoint,
                    heightRatio
                );
                setImageUrl(imageUrlWithResizeOptions);

                preloadImage(imageUrlWithResizeOptions, setImageLoadingState);
            }
        }
    }, [entry?.target, inView]);

    return (
        <div
            className={combineClasses([styles.wrapper, boundsClass, props.className])}
            ref={ref}
        >
            {/* TODO: Set Schema ImageObject */}
            {/* Image */}
            {isBrowserContext() && imageLoadingState === ImageLoadingState.LOADED ? (
                <img
                    className={imgStyles}
                    src={imageUrl}
                    alt={props.alt}
                    loading="lazy"
                />
            ) : null}

            {!isBrowserContext() && props.src ? (
                <img
                    className={styles.image}
                    src={fallbackUrl}
                    alt={props.alt}
                    loading="lazy"
                />
            ) : null}

            {/* Loading */}
            {imageLoadingState === ImageLoadingState.LOADING ? <Spinner /> : null}

            {/* Error */}
            {imageLoadingState === ImageLoadingState.ERROR ? <div className={styles.error}>Error</div> : null}

            {/* TODO: Set the .ratio background color based on the backgroundColor from the media */}
            {typeof props.aspectRatioX !== "undefined" && typeof props.aspectRatioY !== "undefined" ? (
                <svg
                    viewBox={`0 0 ${props.aspectRatioX} ${props.aspectRatioY}`}
                    version="1.1"
                    className={styles.ratio}
                    preserveAspectRatio="xMidYMid"
                    xmlSpace="preserve"
                ></svg>
            ) : null}
        </div>
    );
};

export default ImageViewer;
